import {
    ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, OnInit, output,
    Signal, signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';

import { ProductDebugComponent } from '../product-debug/product-debug.component';
import { ProductViewSizeSelectorComponent } from '../product-view-size-selector/product-view-size-selector.component';

import { ProductViewBadgeComponent } from './components/product-view-badge/product-view-badge.component';
import { ProductViewFooterComponent } from './components/product-view-footer/product-view-footer.component';
import { ActionBadge, ProductViewConfig, ViewModelProductView } from './models/product-view-config.model';
import { MediaProductViewOptions } from './models/product-view-media.model';
import { ProductViewNavigationService } from './services/product-view-navigation.service';
import { getTemplateConfigView } from './services/product-view-template-config.service';
import { ProductViewService } from './services/product-view.service';
import { getMediaProductView } from './transformers/product-view-media.model.transformer';
import { productViewTransformer } from './transformers/product-view.model.transformer';

import { getDebugBarShowProductsInfoStatus } from '@core/infrastructure/state/core.selectors';
import { CategoriesSelectors } from '@features/category';
import { Category } from '@features/category/domain/models/category.models';
import { MenuCategory } from '@features/category/domain/models/menu-category.model';
import { ProductPublicModels } from '@features/products/product';
import { ProductsArrayPublicSelectors } from '@features/products/productsArray';
import { InViewportDirective } from '@layout/ui/directives/inviewport.directive';
import { UiService } from '@layout/ui/services/ui.service';
import { ViewItemService, viewItemServiceConfig } from '@shared/analytics/COMMON/services/product-view/view-item.service';
import { ImageMediaService, MediaSkeletonComponent, ProductCardComponent } from '@shared/media';
import { MediaRatioSkeleton } from '@shared/media/models/media.models';
import { PublicNavigation } from '@shared/navigation';
import { ProductMapPrices } from '@shared/prices/models/product-prices.model';
import { getPricesProductSyncUseCase } from '@shared/prices/use-cases/get-prices.use-cases';
import { xmediaDefault0UseCase } from '@shared/product/application/use-cases/xmedia-default-0.use-case';
import { CategoryConfiguration, GridProductStyle } from '@shared/product/models';
import { ProductLoaderService } from '@shared/product/services/product-loader-service.impl';
import { openNoStocksModalsUseCase } from '@shared/product/use-cases/product-modals.use-cases';

@Component({
    selector: 'product-view',
    templateUrl: './product-view.component.html',
    imports: [
        MediaSkeletonComponent,
        ProductCardComponent,
        ProductViewFooterComponent,
        ProductViewBadgeComponent,
        InViewportDirective,
        // WishlistFeatureModule, // NOTE: Esto provoca múltiples instancias de WishlistFeatureModule
        ProductDebugComponent,
        ProductViewSizeSelectorComponent,
    ],
    providers: [
        ProductViewService,
        ProductViewNavigationService,
        ViewItemService,
        UiService,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class ProductViewComponent implements OnInit {
    // INPUTS
    public productId = input.required<number>();

    public product = input<ProductPublicModels.PublicProduct>();

    public defaultImageType = input<string>();

    public config = input<ProductViewConfig>();

    public categoryConfig = input<CategoryConfiguration>();

    public elementIndex = input<number>();

    public productStyle = input<GridProductStyle>();

    public defaultSet0Activated = input<boolean>(false);

    public category = input<Category | MenuCategory>();

    public xmediaDefaultSet$$ = computed(() => {
        const product = this.product() ?? this.productById$$();
        if (product) {
            const category = this.category();
            const defaultSet0Activated = this.defaultSet0Activated();
            return xmediaDefault0UseCase(product, category, defaultSet0Activated); // o es 0 o null
        }
    });

    public readonly clickActionBadge = output<{ type: ActionBadge; product: ProductPublicModels.PublicProduct }>();

    // LOCAL
    public vm$$: Signal<ViewModelProductView>;

    public priceInfoView$: Observable<ProductMapPrices>;

    public showDiscount = false;

    public showContentProjected = true;

    public requestProduct = false;

    public hasInView = false;

    public ratio: MediaRatioSkeleton;

    public positionMediaClass: string;

    public showQuicklyisActive = false;

    protected readonly productViewService = inject(ProductViewService);

    protected readonly store = inject(Store);

    private readonly productViewNavigationService = inject(ProductViewNavigationService);

    private readonly productLoaderService = inject(ProductLoaderService);

    protected readonly elementRef = inject(ElementRef);

    protected readonly imageMediaService = inject(ImageMediaService);

    private readonly routerNavigation = inject(PublicNavigation);

    public categories$$ = this.store.selectSignal(CategoriesSelectors.getCategoriesNavigation);

    // Se incluyen los thresholds para analítica para mantener un solo sistema de observadores
    public viewThreshold = [
        0, // Funcionalidad
        ...viewItemServiceConfig.nonExactThreshold, // Analítica
    ];

    private readonly productById$$ = computed(() => {
        const productId = this.productId();
        if (!productId) return null;
        const productById = this.store.selectSignal(ProductsArrayPublicSelectors.getProductById(productId))();
        if (!productById) return null;

        return productViewTransformer(
            productById,
            this.productViewService.setDefinedColor(productById, this.config())
        );
    });

    // Información de depuración //
    public isDebugBarShowProductInfoActive$$ = this.store.selectSignal(getDebugBarShowProductsInfoStatus);

    public inViewportExtraOptions = { minVisibleTime: 500 };

    public hasInViewMinTime$$ = signal(false);

    // The actionWhenVisible methods requires an Observable so it can wait for the product to be loaded. It's a tricky way, sorry.
    protected readonly productObs$ = toObservable(this.product);

    public ngOnInit(): void {
        this.ratio = this.config()?.media?.ratio ?? '3-4';

        const priority = this.imageMediaService.onlyOnePriorityImgLoaded;
        if (priority) {
            this.imageMediaService.setImgPriority();
        }

        if (this.product()) {
            this.requestProduct = true;
            this.setViewModelByProduct(this.product());
        } else {
            this.setViewModelByProductId(priority);
        }
    }

    public viewBadgeAction({ event, type }, vm) {
        switch (type) {
            case ActionBadge.NAVIGATION:
                this.loadProduct(event, vm);
                break;

            case ActionBadge.ADDCART:
                this.clickActionBadge.emit({ type, product: vm.product });
                break;

            case ActionBadge.SHOWQUICKLY:
                this.toggleShowQuickly(event);
                break;
        }
    }

    public loadProduct(event: ProductPublicModels.PublicBannerLink, vm: ViewModelProductView): void {
        this.productViewNavigationService.navigate(
            vm.product,
            vm.templateConfig,
            this.categoryConfig()?.category?.id,
            event,
        );
    }

    public toggleShowQuickly(event) {
        event.preventDefault();
        this.showQuicklyisActive = !this.showQuicklyisActive;
    }

    protected containerIsInView() {
        this.hasInView = true;
        this.hasInViewMinTime$$.set(true);

        // Si recibimos un Producto no se debe solicitar de forma async
        if (!(this.requestProduct || this.product())) {
            this.productLoaderService.requestProduct(this.productId());
            this.requestProduct = true;
        }
    }

    protected containerOutInView() {
        this.hasInView = false;
    }

    /** Analítica */
    protected productIsInOrOutView(intersectionEntry: IntersectionObserverEntry) {
        this.productViewService.actionWhenVisible(of(this.vm$$()), this.elementRef, intersectionEntry);
    }

    public onMediaError() {
        console.error('ERROR 404: The product view component does not have a valid media URL to display the image on the page. The image returned a 404 error and does not exist.', this.productId());
    }

    private getClassAlignMedia(configMedia, medias) {
        if (medias.video?.clazz?.type === 'videoDouble' && configMedia?.alignBottom) {
            return 'media-video-double-bottom';
        }

        if (medias.image?.clazz?.type === 'double' && configMedia?.alignBottom) {
            return 'media-double-bottom';
        }
        return '';
    }

    private setViewModelByProduct(product) {
        this.vm$$ = computed(() => {
            if (this.hasInViewMinTime$$()) {
                return this.generateViewModel(product, false);
            }
        });
    }

    protected setViewModelByProductId(priority) {
        this.vm$$ = computed(() => {
            const productById = this.productById$$();
            if (productById && (this.hasInViewMinTime$$() || priority)) {
                return this.generateViewModel(productById, priority);
            }
        });
    }

    protected generateViewModel(product: ProductPublicModels.PublicProduct, priority: boolean): ViewModelProductView {
        const prices = getPricesProductSyncUseCase(this.store, product);
        const config = this.config();
        const defaultImageTypeByConfig = this.categoryConfig()?.productConf?.get(+product.id)?.defaultImageType;
        const defaultImageType = defaultImageTypeByConfig ?? this.defaultImageType();
        const options: MediaProductViewOptions = {
            discardVideos: config.discardVideos,
            discardDoubleVideo: config.discardDoubleVideo,
            discardFallback: config.discardFallback,
            discardDoubleImage: config.discardDoubleImage,
            secondaryImage: config.showSecondImageIfPossible,
        };

        if (this.defaultSet0Activated()) {
            const xMediaSet = this.xmediaDefaultSet$$();
            options.set = xMediaSet;
        }

        const mediaProductAssets = {
            showMediaCarousel: config.showMediaCarousel,
            location: config.location,
            imageType: config.imageType,
            imageTypeAlternative: config.imageTypeAlternative,
            color: product.status.selectedColor,
            defaultImageType: defaultImageType ?? product.defaultImageType,
            options,
        };
        const medias = getMediaProductView(mediaProductAssets);
        const idMedia = medias.video?.idMedia || medias.image?.idMedia;

        const templateConfig = getTemplateConfigView(
            product,
            config,
            this.categoryConfig(),
            this.store,
            !!medias?.video,
            priority,
            idMedia,
        );

        this.positionMediaClass = this.getClassAlignMedia(config?.media, medias);

        if (!medias?.image?.path) {
            console.error('ERROR CONFIG: The product view component does not have a URL to display the image.', this.productId());
        }

        const linkHref = this.getLinkHref(product, idMedia);

        return {
            medias, product, templateConfig, prices, linkHref,
        };
    }

    public addToCart(data: {
        size: ProductPublicModels.PublicProductSize;
        product: ProductPublicModels.PublicProduct & { idMedia?: string };
    }): void {
        this.productViewService.actionAddToCartProductView(
            this.getSelectedSize(data.size, data.product),
            this.config(),
            undefined,
        );
    }

    public showAvailability(data: {
        size: ProductPublicModels.PublicProductSize;
        product: ProductPublicModels.PublicProduct;
    }): void {
        openNoStocksModalsUseCase(this.store, this.getSelectedSize(data.size, data.product), this.config()?.eventFrom);
    }

    protected getSelectedSize(
        size: ProductPublicModels.PublicProductSize,
        product: ProductPublicModels.PublicProduct
    ): ProductPublicModels.PublicProduct {
        return {
            ...product,
            status: {
                ...product?.status,
                selectedSize: size,
            },
        };
    }

    private getLinkHref(product: ProductPublicModels.PublicProduct, idMedia: string): string {
        let linkHref = product?.locationPath;

        const bannerLinkStudio = product?.bannerLinks?.find(b => b.idMedia === idMedia);
        if (bannerLinkStudio) {
            if (bannerLinkStudio.datatype === 'categoryLink') {
                const category = this.categories$$().find((cat) => cat.id === bannerLinkStudio.id);
                linkHref = category?.locationPath;
            } else if (bannerLinkStudio.datatype === 'productLink') {
                const baseUrl = this.routerNavigation.getBaseUrl();
                linkHref = `${baseUrl}product-l${bannerLinkStudio.id}`;
            }
        }

        return linkHref;
    }
}
